import React, { useCallback, useEffect, useMemo, useState } from "react";

import SearchBar from "./SearchBar.jsx";

const MODE_COPY = {
  human: {
    title: "Escolhas da Raça Humano",
    subtitle: "Escolha 1 perícia e 1 Talento de Origem para concluir os benefícios raciais obrigatórios.",
    steps: [
      { key: "skill", label: "Perícia" },
      { key: "feat", label: "Talento de Origem" },
    ],
  },
  "custom-lineage": {
    title: "Escolhas da Linhagem Customizada",
    subtitle: "Defina a identidade da linhagem, escolha o benefício racial e depois selecione 1 Talento de Origem.",
    steps: [
      { key: "details", label: "Identidade" },
      { key: "benefit", label: "Benefício" },
      { key: "feat", label: "Talento de Origem" },
    ],
  },
  "background-custom": {
    title: "Escolhas do Custom Background",
    subtitle: "Defina perícias, ferramenta, equipamento e Talento de Origem no mesmo fluxo.",
    steps: [
      { key: "skills", label: "Perícias" },
      { key: "tooling", label: "Ferramenta e Equipamento" },
      { key: "feat", label: "Talento de Origem" },
    ],
  },
};

function normalizeDualValues(values) {
  const normalized = Array.isArray(values) ? values.filter(Boolean).slice(0, 2) : [];
  return [normalized[0] || "", normalized[1] || ""];
}

function buildInitialDraft(mode, initialSelection) {
  const selectedFeat = typeof initialSelection?.feat === "string" ? initialSelection.feat : "";
  const selectedSkill = typeof initialSelection?.skill === "string" ? initialSelection.skill : "";
  const selectedBenefit = initialSelection?.benefitType === "darkvision" ? "darkvision" : "skill";

  return {
    benefitType: mode === "custom-lineage" ? selectedBenefit : "skill",
    skill: selectedSkill,
    feat: selectedFeat,
    lineageName: typeof initialSelection?.lineageName === "string" ? initialSelection.lineageName : "",
    size: typeof initialSelection?.size === "string" && initialSelection.size ? initialSelection.size : "Medium",
    skills: normalizeDualValues(initialSelection?.skills),
    tool: typeof initialSelection?.tool === "string" ? initialSelection.tool : "",
    customTool: typeof initialSelection?.customTool === "string" ? initialSelection.customTool : "",
    equipmentNotes: typeof initialSelection?.equipmentNotes === "string" ? initialSelection.equipmentNotes : "",
  };
}

function isBenefitDraftValid(mode, draft) {
  if (mode === "human") return Boolean(draft.skill);
  if (mode !== "custom-lineage") return true;
  if (draft.benefitType === "darkvision") return true;
  return draft.benefitType === "skill" && Boolean(draft.skill);
}

function isStepValid(mode, stepKey, draft) {
  if (stepKey === "skill") return Boolean(draft.skill);
  if (stepKey === "details") return Boolean(draft.size);
  if (stepKey === "benefit") return isBenefitDraftValid(mode, draft);
  if (stepKey === "tooling") {
    if (draft.tool !== "__custom") return true;
    return Boolean(String(draft.customTool || "").trim());
  }
  if (stepKey === "feat") {
    if (!draft.feat) return false;
    if (mode === "human") return Boolean(draft.skill);
    if (mode === "custom-lineage") return isBenefitDraftValid(mode, draft);
    return true;
  }
  return true;
}

function getFeatDescriptionText(description) {
  if (typeof description !== "string") return "";
  return description
    .replace(/\\n/g, "\n")
    .replace(/\r\n?/g, "\n")
    .replace(/\*\*/g, "")
    .trim();
}

function SingleSkillStep({ skills, selectedSkill, onSelectSkill }) {
  return (
    <>
      <div className="choice-feat-step-title">Escolha 1 perícia</div>
      <div className="cb-skill-check-grid">
        {skills.map((skill) => {
          const checked = selectedSkill === skill;
          return (
            <label
              key={skill}
              className={`cb-skill-check${checked ? " cb-skill-check--active" : ""}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onSelectSkill(checked ? "" : skill)}
              />
              <span>{skill}</span>
            </label>
          );
        })}
      </div>
    </>
  );
}

function LineageDetailsStep({ lineageName, size, onChangeLineageName, onChangeSize }) {
  return (
    <>
      <div className="choice-feat-step-title">Defina a linhagem</div>
      <label className="cl-label">
        Nome da Linhagem
        <input
          type="text"
          className="cl-input"
          placeholder="Ex: Meio-Dragão das Cinzas"
          value={lineageName}
          onChange={(event) => onChangeLineageName(event.target.value)}
        />
      </label>

      <div className="cl-label">
        Tamanho
        <div className="cl-radio-row">
          <label className="cl-radio-label">
            <input
              type="radio"
              name="choice-feat-lineage-size"
              value="Small"
              checked={size === "Small"}
              onChange={() => onChangeSize("Small")}
            />
            Pequeno (Small)
          </label>
          <label className="cl-radio-label">
            <input
              type="radio"
              name="choice-feat-lineage-size"
              value="Medium"
              checked={size === "Medium"}
              onChange={() => onChangeSize("Medium")}
            />
            Médio (Medium)
          </label>
        </div>
      </div>

      <div className="choice-feat-help">
        O nome da linhagem é opcional, mas o tamanho escolhido será refletido no painel lateral e no resumo final.
      </div>
    </>
  );
}

function BenefitStep({ skills, selectedBenefitType, selectedSkill, onSelectBenefitType, onSelectSkill }) {
  return (
    <>
      <div className="choice-feat-step-title">Escolha 1 benefício racial</div>
      <div className="choice-feat-benefit-grid">
        <button
          type="button"
          className={`choice-feat-benefit-card${selectedBenefitType === "skill" ? " selected" : ""}`}
          onClick={() => onSelectBenefitType("skill")}
        >
          <span className="card-name">Perícia</span>
          <span className="card-detail">Escolha 1 perícia da lista.</span>
        </button>

        <button
          type="button"
          className={`choice-feat-benefit-card${selectedBenefitType === "darkvision" ? " selected" : ""}`}
          onClick={() => onSelectBenefitType("darkvision")}
        >
          <span className="card-name">Visão no Escuro 60ft</span>
          <span className="card-detail">Receba Visão no Escuro com alcance de 60ft.</span>
        </button>
      </div>

      {selectedBenefitType === "skill" ? (
        <div className="choice-feat-skill-wrap">
          <div className="choice-feat-step-subtitle">Selecione a perícia da linhagem</div>
          <div className="cb-skill-check-grid">
            {skills.map((skill) => {
              const checked = selectedSkill === skill;
              return (
                <label
                  key={skill}
                  className={`cb-skill-check${checked ? " cb-skill-check--active" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onSelectSkill(checked ? "" : skill)}
                  />
                  <span>{skill}</span>
                </label>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="choice-feat-help">Benefício selecionado: Visão no Escuro 60ft.</div>
      )}
    </>
  );
}

function BackgroundSkillsStep({ skills, selectedSkills, onToggleSkill }) {
  return (
    <>
      <div className="choice-feat-step-title">Escolha até 2 perícias</div>
      <div className="cb-skill-check-grid">
        {skills.map((skill) => {
          const checked = selectedSkills.includes(skill);
          const disabled = !checked && selectedSkills.length >= 2;
          return (
            <label
              key={skill}
              className={`cb-skill-check${checked ? " cb-skill-check--active" : ""}${disabled ? " cb-skill-check--disabled" : ""}`}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={() => onToggleSkill(skill)}
              />
              <span>{skill}</span>
            </label>
          );
        })}
      </div>
      <div className="choice-feat-help">{selectedSkills.length}/2 perícias selecionadas.</div>
    </>
  );
}

function BackgroundToolingStep({ tools, selectedTool, customTool, equipmentNotes, onSetTool, onSetCustomTool, onSetEquipmentNotes }) {
  return (
    <>
      <div className="choice-feat-step-title">Ferramenta e equipamento</div>
      <label className="cl-label">
        Ferramenta
        <select
          className="cl-select"
          value={selectedTool}
          onChange={(event) => onSetTool(event.target.value)}
        >
          <option value="">— Selecione —</option>
          {tools.map((tool) => (
            <option key={tool} value={tool}>{tool}</option>
          ))}
          <option value="__custom">Outra (digitar)</option>
        </select>
      </label>

      {selectedTool === "__custom" ? (
        <label className="cl-label">
          Ferramenta personalizada
          <input
            type="text"
            className="cl-input"
            placeholder="Ex: Herbalism Kit"
            value={customTool}
            onChange={(event) => onSetCustomTool(event.target.value)}
          />
        </label>
      ) : null}

      <label className="cl-label">
        Equipamento
        <div className="cb-equip-fixed">50 GP (padrão)</div>
        <textarea
          className="cl-input cb-equip-notes"
          rows={3}
          placeholder="Notas sobre equipamento extra (opcional)"
          value={equipmentNotes}
          onChange={(event) => onSetEquipmentNotes(event.target.value)}
        />
      </label>

      <div className="choice-feat-help">
        As notas de equipamento serão exibidas no resumo do background customizado.
      </div>
    </>
  );
}

function FeatStep({ featQuery, onChangeQuery, filteredFeats, selectedFeat, onSelectFeat }) {
  return (
    <>
      <div className="choice-feat-step-title">Escolha 1 Talento de Origem</div>
      <SearchBar
        value={featQuery}
        onChange={onChangeQuery}
        placeholder="Buscar talento de origem..."
      />

      <div className="choice-feat-list">
        {filteredFeats.map((feat) => {
          const isSelected = selectedFeat === feat.name;
          const description = getFeatDescriptionText(feat.description);
          return (
            <button
              key={feat.name}
              type="button"
              className={`choice-feat-item${isSelected ? " choice-feat-item--active" : ""}`}
              onClick={() => onSelectFeat(feat.name)}
            >
              <span className="choice-feat-item-head">
                <span className="choice-feat-item-name">{feat.name}</span>
                {feat.source ? <span className="choice-feat-item-source">{feat.source}</span> : null}
              </span>
              {description ? <span className="choice-feat-item-desc">{description}</span> : null}
            </button>
          );
        })}

        {filteredFeats.length === 0 && (
          <div className="choice-feat-empty">
            Nenhum Talento de Origem encontrado para "{featQuery}".
          </div>
        )}
      </div>
    </>
  );
}

export default function ChoiceFeatModal({
  isOpen,
  mode,
  skills,
  tools = [],
  feats,
  initialSelection,
  onClose,
  onConfirm,
}) {
  const [stepIndex, setStepIndex] = useState(0);
  const [draft, setDraft] = useState(() => buildInitialDraft(mode, initialSelection));
  const [featQuery, setFeatQuery] = useState("");

  const copy = MODE_COPY[mode] || MODE_COPY.human;
  const steps = useMemo(() => copy.steps, [copy]);
  const currentStep = steps[stepIndex]?.key || "feat";

  useEffect(() => {
    if (!isOpen) return;

    setStepIndex(0);
    setDraft(buildInitialDraft(mode, initialSelection));
    setFeatQuery("");
  }, [isOpen, mode, initialSelection]);

  useEffect(() => {
    if (!isOpen) return;

    function handleEscape(event) {
      if (event.key === "Escape") onClose?.();
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const handleBackdrop = useCallback((event) => {
    if (event.target === event.currentTarget) onClose?.();
  }, [onClose]);

  const sortedFeats = useMemo(() => {
    if (!Array.isArray(feats)) return [];
    return [...feats].sort((left, right) => left.name.localeCompare(right.name, "pt-BR"));
  }, [feats]);

  const filteredFeats = useMemo(() => {
    const query = featQuery.trim().toLowerCase();
    if (!query) return sortedFeats;

    return sortedFeats.filter((feat) => {
      const name = String(feat.name || "").toLowerCase();
      const description = String(feat.description || "").toLowerCase();
      return name.includes(query) || description.includes(query);
    });
  }, [sortedFeats, featQuery]);

  const selectedBackgroundSkills = draft.skills.filter(Boolean);
  const canContinue = isStepValid(mode, currentStep, draft);
  const canConfirm = isStepValid(mode, "feat", draft);

  function patchDraft(patch) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  function handleContinue() {
    if (!canContinue) return;
    setStepIndex((current) => Math.min(current + 1, steps.length - 1));
  }

  function handleBack() {
    setStepIndex((current) => Math.max(current - 1, 0));
  }

  function handleToggleBackgroundSkill(skill) {
    setDraft((current) => {
      const selected = current.skills.filter(Boolean);
      if (selected.includes(skill)) {
        return { ...current, skills: normalizeDualValues(selected.filter((entry) => entry !== skill)) };
      }
      if (selected.length >= 2) return current;
      return { ...current, skills: normalizeDualValues([...selected, skill]) };
    });
  }

  function handleSelectBenefit(nextBenefitType) {
    patchDraft({
      benefitType: nextBenefitType,
      skill: nextBenefitType === "darkvision" ? "" : draft.skill,
    });
  }

  function handleConfirm() {
    if (!canConfirm) return;

    onConfirm?.({
      benefitType: mode === "background-custom" ? null : draft.benefitType,
      skill: draft.benefitType === "skill" ? draft.skill : "",
      feat: draft.feat,
      lineageName: draft.lineageName,
      size: draft.size,
      skills: normalizeDualValues(draft.skills),
      tool: draft.tool,
      customTool: draft.tool === "__custom" ? String(draft.customTool || "").trim() : "",
      equipmentNotes: String(draft.equipmentNotes || "").trim(),
    });
  }

  function renderStep() {
    if (currentStep === "skill") {
      return (
        <SingleSkillStep
          skills={skills}
          selectedSkill={draft.skill}
          onSelectSkill={(value) => patchDraft({ skill: value })}
        />
      );
    }

    if (currentStep === "details") {
      return (
        <LineageDetailsStep
          lineageName={draft.lineageName}
          size={draft.size}
          onChangeLineageName={(value) => patchDraft({ lineageName: value })}
          onChangeSize={(value) => patchDraft({ size: value })}
        />
      );
    }

    if (currentStep === "benefit") {
      return (
        <BenefitStep
          skills={skills}
          selectedBenefitType={draft.benefitType}
          selectedSkill={draft.skill}
          onSelectBenefitType={handleSelectBenefit}
          onSelectSkill={(value) => patchDraft({ skill: value })}
        />
      );
    }

    if (currentStep === "skills") {
      return (
        <BackgroundSkillsStep
          skills={skills}
          selectedSkills={selectedBackgroundSkills}
          onToggleSkill={handleToggleBackgroundSkill}
        />
      );
    }

    if (currentStep === "tooling") {
      return (
        <BackgroundToolingStep
          tools={tools}
          selectedTool={draft.tool}
          customTool={draft.customTool}
          equipmentNotes={draft.equipmentNotes}
          onSetTool={(value) => patchDraft({ tool: value, customTool: value === "__custom" ? draft.customTool : "" })}
          onSetCustomTool={(value) => patchDraft({ customTool: value })}
          onSetEquipmentNotes={(value) => patchDraft({ equipmentNotes: value })}
        />
      );
    }

    return (
      <FeatStep
        featQuery={featQuery}
        onChangeQuery={setFeatQuery}
        filteredFeats={filteredFeats}
        selectedFeat={draft.feat}
        onSelectFeat={(value) => patchDraft({ feat: value })}
      />
    );
  }

  if (!isOpen) return null;

  return (
    <div className="sc-features-overlay choice-feat-overlay" onClick={handleBackdrop} role="dialog" aria-modal="true">
      <div className="sc-features-panel choice-feat-panel">
        <button className="sc-features-close" onClick={() => onClose?.()} aria-label="Fechar">×</button>

        <div className="choice-feat-progress">Passo {stepIndex + 1} de {steps.length}</div>
        <h3 className="sc-features-title">{copy.title}</h3>
        <p className="sc-features-subtitle">{copy.subtitle}</p>

        <div className="choice-feat-steps" aria-hidden="true">
          {steps.map((step, index) => {
            const isActive = index === stepIndex;
            const isDone = index < stepIndex;
            return (
              <span
                key={step.key}
                className={`choice-feat-step-dot${isActive ? " choice-feat-step-dot--active" : ""}${isDone ? " choice-feat-step-dot--done" : ""}`}
              >
                {step.label}
              </span>
            );
          })}
        </div>

        <div className="choice-feat-content">
          {renderStep()}
        </div>

        <div className="choice-feat-actions">
          <button type="button" className="wiz-btn" onClick={() => onClose?.()}>
            Cancelar
          </button>

          {stepIndex > 0 ? (
            <button type="button" className="wiz-btn" onClick={handleBack}>
              ← Voltar
            </button>
          ) : null}

          {currentStep !== "feat" ? (
            <button
              type="button"
              className="wiz-btn primary"
              disabled={!canContinue}
              onClick={handleContinue}
            >
              Continuar →
            </button>
          ) : (
            <button
              type="button"
              className="wiz-btn primary"
              disabled={!canConfirm}
              onClick={handleConfirm}
            >
              Confirmar e aplicar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
